// SchadenKarten.tsx — mobile Ansicht der Schadenlisten (Telefon, ≤ 768 px):
// Karten statt Tabelle, Bauform wie die Kontakt-Karten (`.ct-cards`/`.ct-card`
// aus contactRedesign.css). Genutzt von der Übersicht /damages und vom
// Schäden-Reiter der Kontaktdetails; beide liefern ihre Zeilen schon gefiltert.

import * as React from "react";
import { REFINED } from "../risikoanalyseStep/tokens";

/** Status-Werte, die als „geschlossen" gelten (Import-Code 'G' + manuelle Volltexte). */
const CLOSED_STATUSES = ["G", "Geschlossen", "Abgeschlossen", "Erledigt"];

export const isClosedStatus = (s?: string): boolean =>
  !!s && CLOSED_STATUSES.some((c) => c.toLowerCase() === s.trim().toLowerCase());

/** Roh-Status in ein lesbares Label übersetzen (Import nutzt 'O'/'G'). */
export const statusLabel = (s?: string): string => {
  const v = (s ?? "").trim();
  if (!v || v === "O") return "Offen";
  if (v === "G") return "Geschlossen";
  return v;
};

/** Schaden-Status als Pille: offen = warnendes Orange, geschlossen = grün. */
export const StatusPill: React.FC<{ status?: string }> = ({ status }) => {
  const closed = isClosedStatus(status);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
        fontFamily: REFINED.fontUI,
        backgroundColor: closed ? REFINED.okBg : "rgba(247,168,35,0.16)",
        color: closed ? "#1c5434" : "#7a4c00",
      }}
    >
      <span
        style={{ width: 7, height: 7, borderRadius: 999, background: closed ? REFINED.ok : "#f7a823" }}
      />
      {statusLabel(status)}
    </span>
  );
};

export type SchadenKarte = {
  id: number;
  status: string;
  schadennr: string;
  /** Fehlt im Kontakt-Reiter — dort wäre es in jeder Karte derselbe Name. */
  kontakt?: string;
  vertrag: string;
  sparte?: string;
  schadensart: string;
  risikoOrt: string;
  schadenDatum: string;
  meldeDatum: string;
};

const Zeile = ({ k, v }: { k: string; v: string }) =>
  v ? (
    <div className="ct-row">
      <span className="ct-k">{k}</span>
      <span className="ct-v">{v}</span>
    </div>
  ) : null;

export function SchadenKarten({
  karten,
  onOpen,
  emptyText,
  hasMore = false,
  loading = false,
  onLoadMore,
  scrollRef,
  scrollId,
}: {
  karten: SchadenKarte[];
  onOpen: (id: number) => void;
  emptyText: React.ReactNode;
  hasMore?: boolean;
  loading?: boolean;
  onLoadMore?: () => void;
  scrollRef?: React.Ref<HTMLDivElement>;
  scrollId?: string;
}) {
  return (
    <div className="ct-cards" id={scrollId} ref={scrollRef}>
      {karten.length === 0 ? (
        <div className="empty">{emptyText}</div>
      ) : (
        karten.map((s) => (
          <div
            key={s.id}
            className="ct-card"
            role="button"
            tabIndex={0}
            onClick={() => onOpen(s.id)}
            onKeyDown={(e) => e.key === "Enter" && onOpen(s.id)}
          >
            <div className="ct-card-head">
              <div className="ct-card-id">
                <div className="ct-card-name">{s.schadennr || "Ohne Schaden-Nr."}</div>
                {s.schadensart && (
                  <div style={{ fontSize: 13, color: "var(--ink-3)", fontWeight: 600 }}>
                    {s.schadensart}
                  </div>
                )}
              </div>
              <StatusPill status={s.status} />
            </div>
            <div className="ct-card-body">
              {s.kontakt !== undefined && <Zeile k="Kontakt" v={s.kontakt} />}
              <Zeile k="Vertrag" v={[s.vertrag, s.sparte].filter(Boolean).join(" · ")} />
              <Zeile k="Risiko / Ort" v={s.risikoOrt} />
              <Zeile k="Schadentag" v={s.schadenDatum} />
              <Zeile k="Meldedatum" v={s.meldeDatum} />
            </div>
          </div>
        ))
      )}
      {hasMore && onLoadMore ? (
        <button className="ct-more" onClick={() => onLoadMore()} disabled={loading}>
          {loading ? "Lädt …" : "Mehr laden"}
        </button>
      ) : null}
    </div>
  );
}
